import Axios from 'axios';
import { User } from './userApi';
import { Branch } from './branchApi';
import { Client } from './clientsApi';
import { postRateImportFile, downloadRateTemplate, sanitizeFilenamePart } from './rateImportApi';

export enum Role {
  Administrator = 'Administrator',
  ProjectManager = 'ProjectManager',
  Supervisor = 'Supervisor',
  Technician = 'Technician',
}

export type LabourCostRate = {
  id: string;
  organizationId: number;
  role: Role;
  rate: number;
  workingAwayFromHomeRate: number;
  effectiveFrom: string;
  createdOn: string;
  updatedOn: string;
  createdByUserId: string;
  updatedByUserId: string;
};

export type LabourCostRateUserOverride = {
  id: string;
  organizationId: number;
  userId: string;
  user?: User;
  rate: number;
  workingAwayFromHomeRate: number;
  effectiveFrom: string;
  createdOn: string;
  updatedOn: string;
  createdByUserId: string;
  updatedByUserId: string;
};

export type LabourBillingRate = {
  id: string;
  organizationId: number;
  role: Role;
  billingRate: number;
  workingAwayFromHomeBillingRate: number;
  effectiveFrom: string;
  clientId?: string;
  client?: Client;
  branchId?: string;
  branch?: Branch;
  isDefault: boolean;
  createdOn: string;
  updatedOn: string;
  createdByUserId: string;
  updatedByUserId: string;
};

export type LabourRates = {
  costRates: LabourCostRate[];
  costRateUserOverrides: LabourCostRateUserOverride[];
  billingRates: LabourBillingRate[];
};

export type LabourCostRateFormType = {
  role: Role;
  rate: number;
  workingAwayFromHomeRate: number;
  effectiveFrom: string;
};

export type LabourCostRateUserOverrideFormType = {
  userId: string;
  rate: number;
  workingAwayFromHomeRate: number;
  effectiveFrom: string;
  user: User;
};

export type LabourBillingRateFormType = {
  role: Role;
  billingRate: number;
  workingAwayFromHomeBillingRate: number;
  clientId?: string;
  branchId?: string;
  isDefault: boolean;
  effectiveFrom: string;
  branch?: Branch;
  client?: Client;
};

export type ImportMode = 'new' | 'update';

export type LabourPairFieldPlan = {
  action: 'insert' | 'update' | 'unchanged';
  newValue: number;
  oldValue?: number;
  newSecondaryValue: number;
  oldSecondaryValue?: number;
  rateId?: string;
  effectiveFrom: string;
  dateStatus: 'past' | 'today' | 'future';
  dateMoved?: boolean;
};

export type RoleRateRowPlan = {
  row: number;
  role: string;
  cost?: LabourPairFieldPlan;
  billing?: LabourPairFieldPlan;
};

export type EmployeeCostRowPlan = {
  row: number;
  userId: string;
  employeeName: string;
  role: string;
  cost?: LabourPairFieldPlan;
};

export type ScopedBillingRowPlan = {
  row: number;
  role: string;
  billing?: LabourPairFieldPlan;
};

export type BulkUpdateRoleRatesResponse = {
  committed: boolean;
  success: boolean;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  ignoredCount: number;
  errorCount: number;
  created: RoleRateRowPlan[];
  updated: RoleRateRowPlan[];
  ignored: { row: number; role?: string; reason: string }[];
  errors: { row: number; role?: string; message: string }[];
  previewToken?: string;
};

export type BulkUpdateEmployeeCostRatesResponse = {
  committed: boolean;
  success: boolean;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  ignoredCount: number;
  errorCount: number;
  created: EmployeeCostRowPlan[];
  updated: EmployeeCostRowPlan[];
  ignored: { row: number; employeeName?: string; reason: string }[];
  errors: { row: number; employeeName?: string; message: string }[];
  previewToken?: string;
};

export type BulkUpdateScopedBillingRatesResponse = {
  committed: boolean;
  success: boolean;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  ignoredCount: number;
  errorCount: number;
  created: ScopedBillingRowPlan[];
  updated: ScopedBillingRowPlan[];
  ignored: { row: number; role?: string; reason: string }[];
  errors: { row: number; role?: string; message: string }[];
  previewToken?: string;
};

export async function addLabourCostRate({
  data,
}: {
  data: LabourCostRateFormType;
}) {
  const res = await Axios.post(`/api/v1/labour/cost-rate`, data);
  return res.data;
}

export async function addLabourCostRateUserOverride({
  data,
}: {
  data: LabourCostRateUserOverrideFormType;
}) {
  const res = await Axios.post(`/api/v1/labour/cost-rate-override`, data);
  return res.data;
}

export async function addLabourBillingRate({
  data,
}: {
  data: LabourBillingRateFormType;
}) {
  const res = await Axios.post(`/api/v1/labour/billing-rate`, data);
  return res.data;
}

export async function getLabourRates(): Promise<LabourRates> {
  const res = await Axios.get(`/api/v1/labour/rates`);
  return res.data;
}

export async function deleteLabourCostRate(id: string) {
  try {
    const response = await Axios.delete(`/api/v1/labour/cost-rates/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete labour cost rate: ${error}`);
  }
}

export async function deleteLabourCostRateUserOverride(id: string) {
  try {
    const response = await Axios.delete(
      `/api/v1/labour/cost-rate-overrides/${id}`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to delete labour cost rate user override: ${error}`
    );
  }
}

export async function deleteLabourBillingRate(id: string) {
  try {
    const response = await Axios.delete(`/api/v1/labour/billing-rates/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete labour billing rate: ${error}`);
  }
}

export async function updateLabourRate({
  id,
  data,
  rateType,
}: {
  id: string;
  data: Partial<
    | LabourCostRateFormType
    | LabourCostRateUserOverrideFormType
    | LabourBillingRateFormType
  >;
  rateType: 'costRate' | 'costRateOverride' | 'billingRate';
}) {
  const res = await Axios.put(`/api/v1/labour/${rateType}/${id}`, data);
  return res.data;
}

export async function compareLabourRoleRatesImport({
  file,
}: {
  file: File;
}): Promise<BulkUpdateRoleRatesResponse> {
  return postRateImportFile('/api/v1/labour/role-rates/import/compare/new', file);
}

export async function compareLabourRoleRatesUpdateImport({
  file,
}: {
  file: File;
}): Promise<BulkUpdateRoleRatesResponse> {
  return postRateImportFile('/api/v1/labour/role-rates/import/compare/update', file);
}

export async function bulkUpdateLabourRoleRates({
  previewToken,
}: {
  previewToken: string;
}): Promise<BulkUpdateRoleRatesResponse> {
  const res = await Axios.post('/api/v1/labour/role-rates/import', { previewToken });
  return res.data;
}

export async function downloadLabourRoleRatesNewTemplate() {
  return downloadRateTemplate(
    '/api/v1/labour/role-rates/import/template/new',
    'restorize_labour_rates_role_new_template.xlsx'
  );
}

export async function downloadLabourRoleRatesUpdateTemplate() {
  return downloadRateTemplate(
    '/api/v1/labour/role-rates/import/template/update',
    'restorize_labour_rates_role_update_template.xlsx'
  );
}

export async function hasExistingRoleRates(): Promise<boolean> {
  const res = await Axios.get('/api/v1/labour/role-rates/has-existing');
  return res.data.exists;
}

// Employee-specific Cost Rates
export async function compareLabourEmployeeCostRatesImport({
  file,
}: {
  file: File;
}): Promise<BulkUpdateEmployeeCostRatesResponse> {
  return postRateImportFile('/api/v1/labour/cost-rate-overrides/import/compare/new', file);
}

export async function compareLabourEmployeeCostRatesUpdateImport({
  file,
}: {
  file: File;
}): Promise<BulkUpdateEmployeeCostRatesResponse> {
  return postRateImportFile('/api/v1/labour/cost-rate-overrides/import/compare/update', file);
}

export async function bulkUpdateLabourEmployeeCostRates({
  previewToken,
}: {
  previewToken: string;
}): Promise<BulkUpdateEmployeeCostRatesResponse> {
  const res = await Axios.post('/api/v1/labour/cost-rate-overrides/import', { previewToken });
  return res.data;
}

export async function downloadLabourEmployeeCostRatesNewTemplate() {
  return downloadRateTemplate(
    '/api/v1/labour/cost-rate-overrides/import/template/new',
    'restorize_labour_rates_employee_new_template.xlsx'
  );
}

export async function downloadLabourEmployeeCostRatesUpdateTemplate() {
  return downloadRateTemplate(
    '/api/v1/labour/cost-rate-overrides/import/template/update',
    'restorize_labour_rates_employee_update_template.xlsx'
  );
}

export async function hasExistingEmployeeCostRates(): Promise<boolean> {
  const res = await Axios.get('/api/v1/labour/cost-rate-overrides/has-existing');
  return res.data.exists;
}

// Branch/Client-specific Billing Rates by Role

export async function compareLabourScopedBillingRatesImport({
  file,
  branchId,
  clientId,
}: {
  file: File;
  branchId?: string;
  clientId?: string;
}): Promise<BulkUpdateScopedBillingRatesResponse> {
  const extraFields: Record<string, string> = {};
  if (branchId) extraFields.branchId = branchId;
  if (clientId) extraFields.clientId = clientId;
  return postRateImportFile(
    '/api/v1/labour/billing-rates/import/compare/new',
    file,
    extraFields
  );
}

export async function compareLabourScopedBillingRatesUpdateImport({
  file,
  branchId,
  clientId,
}: {
  file: File;
  branchId?: string;
  clientId?: string;
}): Promise<BulkUpdateScopedBillingRatesResponse> {
  const extraFields: Record<string, string> = {};
  if (branchId) extraFields.branchId = branchId;
  if (clientId) extraFields.clientId = clientId;
  return postRateImportFile(
    '/api/v1/labour/billing-rates/import/compare/update',
    file,
    extraFields
  );
}

export async function bulkUpdateLabourScopedBillingRates({
  previewToken,
}: {
  previewToken: string;
}): Promise<BulkUpdateScopedBillingRatesResponse> {
  const res = await Axios.post('/api/v1/labour/billing-rates/import', { previewToken });
  return res.data;
}

export function getLabourBillingRatesTemplateFilename(
  mode: ImportMode,
  branchName?: string,
  clientName?: string
): string {
  const scopeParts = [branchName, clientName].filter(Boolean).map(sanitizeFilenamePart);
  return `restorize_labour_rates_billing_${mode}_${scopeParts.join('-')}_template.xlsx`;
}

export async function downloadLabourScopedBillingRatesNewTemplate({
  branchId,
  clientId,
  branchName,
  clientName,
}: {
  branchId?: string;
  clientId?: string;
  branchName?: string;
  clientName?: string;
}) {
  return downloadRateTemplate(
    '/api/v1/labour/billing-rates/import/template/new',
    getLabourBillingRatesTemplateFilename('new', branchName, clientName),
    { branchId, clientId }
  );
}

export async function downloadLabourScopedBillingRatesUpdateTemplate({
  branchId,
  clientId,
  branchName,
  clientName,
}: {
  branchId?: string;
  clientId?: string;
  branchName?: string;
  clientName?: string;
}) {
  return downloadRateTemplate(
    '/api/v1/labour/billing-rates/import/template/update',
    getLabourBillingRatesTemplateFilename('update', branchName, clientName),
    { branchId, clientId }
  );
}

export async function hasExistingScopedBillingRates({
  branchId,
  clientId,
}: {
  branchId?: string;
  clientId?: string;
}): Promise<boolean> {
  const res = await Axios.get('/api/v1/labour/billing-rates/has-existing', {
    params: { branchId, clientId },
  });
  return res.data.exists;
}
